import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  User, 
  Mail, 
  Phone,
  ChevronLeft,
  ChevronRight,
  Check,
  Utensils,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  // Booking data
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [partySize, setPartySize] = useState('2');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [maxPartySize, setMaxPartySize] = useState(10);
  
  // Guest info
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');

  // Fetch availability when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailability = async (date) => {
    setSlotsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/availability/${date}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
        setMaxPartySize(data.max_party_size || 10);
      }
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      toast.error('Impossible de charger les disponibilités');
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!guestName || !guestEmail) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reservations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          guest_name: guestName,
          guest_email: guestEmail,
          guest_phone: guestPhone || null,
          date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: selectedTime,
          party_size: parseInt(partySize),
          special_requests: specialRequests || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Réservation confirmée !');
        navigate(`/confirmation/${data.reservation_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la réservation');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = selectedDate && selectedTime;
  const canProceedStep2 = guestName && guestEmail;

  const pageVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <Utensils className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span className="font-bold text-xl text-zinc-900" style={{ fontFamily: 'Manrope' }}>
                L'Ange Di Vin
              </span>
            </Link>
            
            <Link to="/">
              <Button variant="ghost" className="text-zinc-600 hover:text-zinc-900">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-zinc-100 py-4">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-center gap-4">
            {[
              { num: 1, label: 'Date & Heure' },
              { num: 2, label: 'Vos informations' },
              { num: 3, label: 'Confirmation' }
            ].map((s, i) => (
              <div key={s.num} className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s.num 
                      ? 'bg-zinc-900 text-white' 
                      : 'bg-zinc-100 text-zinc-400'
                  }`}
                  data-testid={`step-${s.num}`}
                >
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`ml-2 text-sm hidden sm:inline ${
                  step >= s.num ? 'text-zinc-900' : 'text-zinc-400'
                }`}>
                  {s.label}
                </span>
                {i < 2 && (
                  <div className={`w-12 h-0.5 ml-4 ${
                    step > s.num ? 'bg-zinc-900' : 'bg-zinc-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          <AnimatePresence mode="wait">
            {/* Step 1: Date & Time */}
            {step === 1 && (
              <motion.div
                key="step1"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-2xl border border-zinc-200 p-8">
                  <h2 
                    className="text-2xl font-semibold text-zinc-900 mb-6"
                    style={{ fontFamily: 'Manrope' }}
                  >
                    Choisissez une date et un horaire
                  </h2>

                  {/* Date Selection */}
                  <div className="mb-8">
                    <Label className="text-zinc-700 mb-3 block">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal h-12 border-zinc-200"
                          data-testid="date-picker-trigger"
                        >
                          <CalendarIcon className="mr-3 h-5 w-5 text-zinc-400" />
                          {selectedDate ? (
                            <span className="text-zinc-900">
                              {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                            </span>
                          ) : (
                            <span className="text-zinc-400">Sélectionnez une date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedDate}
                          onSelect={(date) => {
                            setSelectedDate(date);
                            setSelectedTime(null);
                          }}
                          disabled={(date) => 
                            isBefore(date, startOfDay(new Date())) || 
                            isBefore(addDays(new Date(), 30), date)
                          }
                          initialFocus
                          data-testid="calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Party Size */}
                  <div className="mb-8">
                    <Label className="text-zinc-700 mb-3 block">Nombre de personnes</Label>
                    <Select value={partySize} onValueChange={setPartySize}>
                      <SelectTrigger 
                        className="w-full h-12 border-zinc-200"
                        data-testid="party-size-select"
                      >
                        <Users className="mr-3 h-5 w-5 text-zinc-400" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: Math.min(maxPartySize, 6) }, (_, i) => i + 1).map((num) => (
                          <SelectItem key={num} value={num.toString()}>
                            {num} {num === 1 ? 'personne' : 'personnes'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-zinc-500 mt-2">
                      Plus de 6 personnes ? Appelez-nous au{' '}
                      <a href="tel:+33745198222" className="text-orange-500 font-medium hover:underline">
                        07 45 19 82 22
                      </a>
                    </p>
                  </div>

                  {/* Time Slots */}
                  <div>
                    <Label className="text-zinc-700 mb-3 block">Horaire</Label>
                    {!selectedDate ? (
                      <p className="text-zinc-400 text-sm">
                        Sélectionnez d'abord une date
                      </p>
                    ) : slotsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <p className="text-zinc-400 text-sm">
                        Aucun créneau disponible pour cette date
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            onClick={() => slot.is_available && setSelectedTime(slot.time)}
                            disabled={!slot.is_available}
                            className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                              selectedTime === slot.time
                                ? 'bg-zinc-900 text-white'
                                : slot.is_available
                                  ? 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                                  : 'bg-zinc-50 text-zinc-300 cursor-not-allowed'
                            }`}
                            data-testid={`time-slot-${slot.time}`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Next Button */}
                  <div className="mt-10 flex justify-end">
                    <Button
                      onClick={() => setStep(2)}
                      disabled={!canProceedStep1}
                      className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-full h-12 px-8"
                      data-testid="next-step-1"
                    >
                      Continuer
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Guest Info */}
            {step === 2 && (
              <motion.div
                key="step2"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-2xl border border-zinc-200 p-8">
                  <h2 
                    className="text-2xl font-semibold text-zinc-900 mb-6"
                    style={{ fontFamily: 'Manrope' }}
                  >
                    Vos informations
                  </h2>

                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-zinc-700 mb-2 block">
                        Nom complet *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                          id="name"
                          value={guestName}
                          onChange={(e) => setGuestName(e.target.value)}
                          placeholder="Jean Dupont"
                          className="pl-11 h-12 border-zinc-200"
                          data-testid="guest-name-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-zinc-700 mb-2 block">
                        Email *
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                          id="email"
                          type="email"
                          value={guestEmail}
                          onChange={(e) => setGuestEmail(e.target.value)}
                          placeholder="jean.dupont@email.com"
                          className="pl-11 h-12 border-zinc-200"
                          data-testid="guest-email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-zinc-700 mb-2 block">
                        Téléphone (optionnel)
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                          id="phone"
                          type="tel"
                          value={guestPhone}
                          onChange={(e) => setGuestPhone(e.target.value)}
                          placeholder="06 12 34 56 78"
                          className="pl-11 h-12 border-zinc-200"
                          data-testid="guest-phone-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="requests" className="text-zinc-700 mb-2 block">
                        Demandes spéciales (optionnel)
                      </Label>
                      <Textarea
                        id="requests"
                        value={specialRequests}
                        onChange={(e) => setSpecialRequests(e.target.value)}
                        placeholder="Allergies, anniversaire, table en terrasse..."
                        className="min-h-[100px] border-zinc-200"
                        data-testid="special-requests-input"
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-8 p-4 bg-zinc-50 rounded-xl">
                    <p className="text-sm font-medium text-zinc-700 mb-2">Récapitulatif</p>
                    <div className="flex flex-wrap gap-4 text-sm text-zinc-600">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {selectedDate && format(selectedDate, 'd MMMM yyyy', { locale: fr })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {selectedTime}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {partySize} {parseInt(partySize) === 1 ? 'personne' : 'personnes'}
                      </span>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="mt-10 flex justify-between">
                    <Button
                      variant="ghost"
                      onClick={() => setStep(1)}
                      className="text-zinc-600"
                      data-testid="back-step-2"
                    >
                      <ChevronLeft className="w-5 h-5 mr-2" />
                      Retour
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={!canProceedStep2 || loading}
                      className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-full h-12 px-8"
                      data-testid="submit-booking"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Réservation...
                        </>
                      ) : (
                        <>
                          Confirmer la réservation
                          <Check className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
